import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Users, Plus, LogOut, UserPlus, Settings, Copy, RefreshCw, AlertTriangle } from "lucide-react";
import { CreateGroupModal } from "./CreateGroupModal";
import { JoinGroupModal } from "./JoinGroupModal";
import { GroupView } from "./GroupView";
import { ErrorModal } from "./ErrorModal";
import { formatLocalDateTime, formatRelativeTime } from "@/lib/date-time";
import { PushNotifications, disablePushBeforeLogout } from "./PushNotifications";

interface User {
  codicePersona: string;
  displayName: string;
  expiresAt?: string;
  welfareLastRefresh?: string;
}

interface Group {
  id: string;
  name: string;
  createdAt: string;
  createdBy: string;
  memberCount?: number;
  userRole?: 'admin' | 'member';
}

interface DashboardProps {
  user: User;
  onLogout: () => void;
}

export function Dashboard({ user, onLogout }: DashboardProps) {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [currentGroup, setCurrentGroup] = useState<Group | null>(null);
  const [userGroups, setUserGroups] = useState<Group[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isReauthLoading, setIsReauthLoading] = useState(false);
  const [isWelfareRefreshLoading, setIsWelfareRefreshLoading] = useState(false);
  const [showReauthModal, setShowReauthModal] = useState(false);
  const [currentUser, setCurrentUser] = useState<User>(user);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [retryAction, setRetryAction] = useState<(() => void) | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await disablePushBeforeLogout();
      onLogout();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "Unable to disable notifications before logout. Please try again.");
      setRetryAction(() => handleLogout);
      setShowErrorModal(true);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const fetchUserInfo = async () => {
    try {
      const response = await fetch("/api/auth/me");
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.user) {
          setCurrentUser(data.user);
        }
      } else if (response.status === 401) {
        // Redirect to login page if unauthorized
        window.location.href = '/login';
      } else {
        const errorData = await response.json();
        setErrorMessage(errorData.error || "Failed to fetch user information");
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error("Error fetching user info:", error);
      setErrorMessage("Network error while fetching user information");
      setShowErrorModal(true);
    }
  };

  // Update user data and refresh session info
  useEffect(() => {
    setCurrentUser(user);
    fetchUserInfo(); // Fetch updated user info
  }, [user]);

  // Fetch user's groups on component mount
  useEffect(() => {
    fetchUserGroups();
  }, []);

  const fetchUserGroups = async () => {
    try {
      const response = await fetch("/api/user/groups");
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setUserGroups(data.groups || []);
        }
      } else {
        const errorData = await response.json();
        setErrorMessage(errorData.error || "Failed to fetch user groups");
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error("Error fetching user groups:", error);
      setErrorMessage("Network error while fetching user groups");
      setShowErrorModal(true);
    }
  };

  // Calculate session status and days until expiry
  const getSessionInfo = () => {
    if (!currentUser.expiresAt) return null;

    const expiryDate = new Date(currentUser.expiresAt);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    return {
      expiryDate,
      daysUntilExpiry,
      isExpired: daysUntilExpiry < 0,
      isWarning: daysUntilExpiry >= 0 && daysUntilExpiry <= 3,
      daysText: daysUntilExpiry < 0 ? `Expired ${Math.abs(daysUntilExpiry)} days ago` :
                daysUntilExpiry === 0 ? 'Expires today' :
                daysUntilExpiry === 1 ? 'Expires tomorrow' :
                `Expires in ${daysUntilExpiry} days`
    };
  };

  const handleReauth = async () => {
    setIsReauthLoading(true);
    try {
      // Call reauth API to invalidate session in database
      const response = await fetch("/api/auth/reauth", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        // Clear client-side storage to prevent redirect loops
        sessionStorage.clear();
        localStorage.clear();

        // Redirect to login for re-authentication
        window.location.href = '/login';
      } else {
        const errorData = await response.json();
        setErrorMessage(errorData.error || "Re-authentication failed");
        setRetryAction(() => handleReauth);
        setShowErrorModal(true);
        setIsReauthLoading(false);
      }
    } catch (error) {
      console.error("Error during re-authentication:", error);
      setErrorMessage("Network error during re-authentication");
      setRetryAction(() => handleReauth);
      setShowErrorModal(true);
      setIsReauthLoading(false);
    }
  };

  const handleWelfareRefresh = async () => {
    setIsWelfareRefreshLoading(true);
    try {
      const response = await fetch("/api/auth/refresh-welfare", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Update current user with new welfare last refresh time
          setCurrentUser(prev => ({
            ...prev,
            welfareLastRefresh: data.welfareLastRefresh,
          }));
        }
      } else {
        const errorData = await response.json();
        setErrorMessage(errorData.error || "Failed to refresh welfare session");
        setRetryAction(() => handleWelfareRefresh);
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error("Error refreshing welfare session:", error);
      setErrorMessage("Network error while refreshing welfare session");
      setRetryAction(() => handleWelfareRefresh);
      setShowErrorModal(true);
    } finally {
      setIsWelfareRefreshLoading(false);
    }
  };

  const handleCreateGroup = async (groupName: string) => {
    setIsLoading(true);
    try {
      const response = await fetch("/api/groups", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name: groupName }),
      });

      if (response.ok) {
        const response_data = await response.json();
        const newGroup = response_data.group;
        await fetchUserGroups(); // Refresh groups list
        setCurrentGroup(newGroup);
        setShowCreateModal(false);
      } else {
        const errorData = await response.json();
        setErrorMessage(errorData.error || "Failed to create group");
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error("Error creating group:", error);
      setErrorMessage("Network error while creating group");
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleJoinGroup = async (inviteCode: string) => {
    setIsLoading(true);
    try {
      const response = await fetch(`/api/groups/join`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ inviteCode }),
      });

      if (response.ok) {
        const response_data = await response.json();
        const group = response_data.group;
        await fetchUserGroups(); // Refresh groups list
        setCurrentGroup(group);
        setShowJoinModal(false);
      } else {
        const errorData = await response.json();
        setErrorMessage(errorData.error || "Failed to join group");
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error("Error joining group:", error);
      setErrorMessage("Network error while joining group");
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLeaveGroup = async () => {
    setCurrentGroup(null);
    await fetchUserGroups(); // Refresh groups list when leaving group
  };

  const handleGroupUpdated = (updatedGroup: Group) => {
    setCurrentGroup((prevGroup) =>
      prevGroup && prevGroup.id === updatedGroup.id ? { ...prevGroup, ...updatedGroup } : prevGroup
    );
    setUserGroups((prevGroups) =>
      prevGroups.map((group) => (group.id === updatedGroup.id ? { ...group, ...updatedGroup } : group))
    );
  };

  const handleCloseErrorModal = () => {
    setShowErrorModal(false);
    setErrorMessage("");
    setRetryAction(null);
  };

  // If user is in a group, show group view
  if (currentGroup) {
    return (
      <GroupView
        group={currentGroup}
        user={user}
        onLeaveGroup={handleLeaveGroup}
        onGroupUpdated={handleGroupUpdated}
      />
    );
  }

  return (
    <div className="min-h-screen w-full min-w-0 bg-background">
      {/* Header */}
      <header className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-3 py-3 sm:px-4 sm:py-4">
          <div className="flex min-w-0 items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2 sm:gap-4">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary">
                <Users className="h-4 w-4 text-primary-foreground" />
              </div>
              <div className="min-w-0">
                <h1 className="truncate text-base font-semibold leading-tight sm:text-xl">360welfare Token Sharing</h1>
                <p className="truncate text-xs leading-tight text-muted-foreground sm:text-sm">
                  Welcome, {user.displayName}
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={handleLogout} disabled={isLoggingOut} className="shrink-0 px-2 sm:px-3">
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto w-full max-w-full px-3 py-6 sm:px-4 sm:py-8">
        <div className="mx-auto min-w-0 max-w-4xl">
          {/* User Info Card */}
          <Card className="mb-4">
            <CardHeader className="px-4 pt-3 pb-2">
              <CardTitle className="flex items-center gap-2 text-sm">
                <Settings className="h-4 w-4" />
                Your Profile
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-3 pt-0">
              <div className="space-y-3">
                <div className="flex min-w-0 items-center justify-between gap-3">
                  <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-0.5">
                    <p className="break-words text-sm font-medium">{user.displayName}</p>
                    <p className="text-xs text-muted-foreground">
                      Codice Persona: {user.codicePersona}
                    </p>
                  </div>
                  <Badge variant="secondary">Active</Badge>
                </div>

                <div className="grid gap-2 sm:grid-cols-2">
                {/* Session Expiry Information */}
                {(() => {
                  const sessionInfo = getSessionInfo();
                  if (sessionInfo) {
                    return (
                      <div className="flex min-w-0 flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2">
                        <div className="flex min-w-0 items-center gap-2">
                          {sessionInfo.isExpired ? (
                            <AlertTriangle className="h-4 w-4 shrink-0 text-red-500" />
                          ) : sessionInfo.isWarning ? (
                            <AlertTriangle className="h-4 w-4 shrink-0 text-yellow-500" />
                          ) : (
                            <div className="h-2 w-2 shrink-0 rounded-full bg-green-500" />
                          )}
                          <div className="min-w-0">
                            <p className="text-xs font-medium">Polimi Session</p>
                            <p className={`text-xs ${
                              sessionInfo.isExpired ? 'text-red-600' :
                              sessionInfo.isWarning ? 'text-yellow-600' :
                              'text-green-600'
                            }`}>
                              {sessionInfo.daysText}
                            </p>
                            <p className="text-xs text-muted-foreground" title={sessionInfo.expiryDate.toLocaleString()}>
                              Expires: {sessionInfo.expiryDate.toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant={sessionInfo.isExpired ? "default" : "outline"}
                          size="sm"
                          onClick={handleReauth}
                          disabled={isReauthLoading}
                          className="h-8 shrink-0 gap-1.5 px-2 text-xs"
                        >
                          <RefreshCw className={`h-4 w-4 ${isReauthLoading ? 'animate-spin' : ''}`} />
                          {isReauthLoading ? 'Updating...' : 'Re-authenticate'}
                        </Button>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Welfare Session Information */}
                {currentUser.welfareLastRefresh && (
                  <div className="flex min-w-0 flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2">
                    <div className="flex min-w-0 items-center gap-2">
                      <div className="h-2 w-2 shrink-0 rounded-full bg-blue-500" />
                      <div className="min-w-0">
                        <p className="text-xs font-medium">Welfare Session</p>
                        <p
                          className="text-xs text-blue-600"
                          title={`Last refreshed: ${formatLocalDateTime(currentUser.welfareLastRefresh)}`}
                        >
                          Last refreshed: {formatRelativeTime(currentUser.welfareLastRefresh)}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleWelfareRefresh}
                      disabled={isWelfareRefreshLoading}
                      className="h-8 shrink-0 gap-1.5 px-2 text-xs"
                    >
                      <RefreshCw className={`h-4 w-4 ${isWelfareRefreshLoading ? 'animate-spin' : ''}`} />
                      {isWelfareRefreshLoading ? 'Refreshing...' : 'Refresh'}
                    </Button>
                  </div>
                )}
                </div>
                <PushNotifications userId={user.codicePersona} />
              </div>
            </CardContent>
          </Card>


          {/* User's Groups */}
          {userGroups.length > 0 && (
            <Card>
              <CardHeader className="p-4 sm:p-6">
                <CardTitle>Your Groups</CardTitle>
                <CardDescription>
                  Groups you're a member of
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
                <div className="space-y-2">
                  {userGroups.map((group, index) => (
                    <div
                      key={group.id || `group-${index}`}
                      className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors cursor-pointer"
                      onClick={() => setCurrentGroup(group)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
                          <Users className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">{group.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {group.memberCount || 0} members • {group.userRole}
                          </p>
                        </div>
                      </div>
                      <span className="text-sm text-muted-foreground">View</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Dashboard Actions */}
          <div className="mb-6 mt-6 sm:mb-8 sm:mt-8">
            <div className="mx-auto grid max-w-2xl grid-cols-2 gap-3 sm:gap-4">
              {/* Create Group Card */}
              <Card className="group hover:shadow-lg transition-all duration-200 cursor-pointer border-2 border-dashed hover:border-primary/50">
                <CardHeader className="p-3 text-center sm:p-6">
                  <div className="mx-auto mb-2 flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 transition-colors group-hover:bg-primary/20 sm:mb-4 sm:h-12 sm:w-12">
                    <Plus className="h-5 w-5 text-primary sm:h-6 sm:w-6" />
                  </div>
                  <CardTitle className="text-sm sm:text-base">Create Group</CardTitle>
                  <CardDescription className="hidden sm:block">
                    Start a new group and invite your friends
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-3 pt-0 text-center sm:p-6 sm:pt-0">
                  <Button
                    onClick={() => setShowCreateModal(true)}
                    size="sm"
                    className="w-full px-2 sm:h-9 sm:px-4"
                    disabled={isLoading}
                  >
                    <span className="sm:hidden">Create</span>
                    <span className="hidden sm:inline">Create New Group</span>
                  </Button>
                </CardContent>
              </Card>

              {/* Join Group Card */}
              <Card className="group hover:shadow-lg transition-all duration-200 cursor-pointer border-2 border-dashed hover:border-primary/50">
                <CardHeader className="p-3 text-center sm:p-6">
                  <div className="mx-auto mb-2 flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 transition-colors group-hover:bg-primary/20 sm:mb-4 sm:h-12 sm:w-12">
                    <UserPlus className="h-5 w-5 text-primary sm:h-6 sm:w-6" />
                  </div>
                  <CardTitle className="text-sm sm:text-base">Join Group</CardTitle>
                  <CardDescription className="hidden sm:block">
                    Join an existing group with an invite code
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-3 pt-0 text-center sm:p-6 sm:pt-0">
                  <Button
                    onClick={() => setShowJoinModal(true)}
                    size="sm"
                    className="w-full px-2 sm:h-9 sm:px-4"
                    disabled={isLoading}
                    variant="outline"
                  >
                    <span className="sm:hidden">Join</span>
                    <span className="hidden sm:inline">Join Existing Group</span>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      {/* Modals */}
      <CreateGroupModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateGroup}
        isLoading={isLoading}
      />

      <JoinGroupModal
        isOpen={showJoinModal}
        onClose={() => setShowJoinModal(false)}
        onSubmit={handleJoinGroup}
        isLoading={isLoading}
      />

      <ErrorModal
        isOpen={showErrorModal}
        onClose={handleCloseErrorModal}
        error={errorMessage}
        onRetry={retryAction || undefined}
        isRetrying={isReauthLoading || isWelfareRefreshLoading || isLoading}
      />
    </div>
  );
}